import React, { useState, useEffect, useCallback } from 'react';
import { Button, Form, Input, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { API_ORIGIN } from '../../../config';

// A lookup list (initiatives or justifications) with an add form.
const OptionList = ({ title, items, loading, form, fieldName, placeholder, emptyText, onAdd, getKey, getText }) => (
  <section className="do-panel">
    <div className="do-panel__head">
      <h2 className="do-panel__title">{title}</h2>
      <span className="do-muted do-num">{items.length} total</span>
    </div>
    <div className="do-panel__body">
      <Form form={form} onFinish={onAdd} layout="vertical">
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <Form.Item name={fieldName} rules={[{ required: true, message: placeholder }]} style={{ flex: 1, marginBottom: 0 }}>
            <Input placeholder={placeholder} aria-label={placeholder} />
          </Form.Item>
          <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={loading}>
            Add
          </Button>
        </div>
      </Form>
    </div>
    {items.length === 0 ? (
      <div className="do-empty" style={{ borderTop: '1px solid var(--do-line)' }}>
        {loading ? 'Loading…' : emptyText}
      </div>
    ) : (
      <ul className="do-optionlist">
        {items.map((item) => (
          <li key={getKey(item)}>{getText(item)}</li>
        ))}
      </ul>
    )}
  </section>
);

// The initiatives and justifications offered on the New direct order form.
const ListsPanel = () => {
  const [initiatives, setInitiatives] = useState([]);
  const [justifications, setJustifications] = useState([]);
  const [initiativeForm] = Form.useForm();
  const [justificationForm] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const fetchLists = useCallback(async () => {
    try {
      setLoading(true);
      const [initResponse, justResponse] = await Promise.all([
        fetch(`${API_ORIGIN}/api/direct-orders/initiatives`, { credentials: 'include' }),
        fetch(`${API_ORIGIN}/api/direct-orders/justifications`, { credentials: 'include' }),
      ]);

      const initData = await initResponse.json();
      const justData = await justResponse.json();

      if (initData.success && Array.isArray(initData.initiatives)) {
        setInitiatives(initData.initiatives.sort((a, b) => a.id - b.id));
      }

      if (justData.success && Array.isArray(justData.justifications)) {
        setJustifications(justData.justifications.sort((a, b) => (a.justificationId ?? a.id) - (b.justificationId ?? b.id)));
      }
    } catch (error) {
      message.error('Failed to load settings data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLists();
  }, [fetchLists]);

  const handleAdd = (path, payloadKey, form, label) => async (values) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_ORIGIN}/api/direct-orders/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [payloadKey]: values[payloadKey] }),
      });
      const data = await response.json();

      if (data.success) {
        message.success(`${label} added`);
        form.resetFields();
        fetchLists();
      } else {
        message.error(data.message || `Failed to add ${label.toLowerCase()}`);
      }
    } catch (error) {
      message.error(`Failed to add ${label.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="do-split">
      <OptionList
        title="Initiatives"
        items={initiatives}
        loading={loading}
        form={initiativeForm}
        fieldName="initiative"
        placeholder="New initiative name"
        emptyText="No initiatives yet"
        onAdd={handleAdd('initiatives', 'initiative', initiativeForm, 'Initiative')}
        getKey={(item) => item.id}
        getText={(item) => item.initiative}
      />
      <OptionList
        title="Justifications"
        items={justifications}
        loading={loading}
        form={justificationForm}
        fieldName="justification"
        placeholder="New justification text"
        emptyText="No justifications yet"
        onAdd={handleAdd('justifications', 'justification', justificationForm, 'Justification')}
        getKey={(item) => item.justificationId ?? item.id}
        getText={(item) => item.justification}
      />
    </div>
  );
};

export default ListsPanel;
